import toast from "react-hot-toast";
import {
  PROMO_CODE_SEND_EMAIL_SMS,
  UPDATE_PROMO_CODE,
  ASSIGN_ATTENDEE_TO_PROMO_CODE,
} from "./mutations";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { Input } from "../../components/inputs/Input";
import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  redirect,
  useFetcher,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import { FormLayout } from "../../layout/FormLayout";
import RadioGroup from "../../components/inputs/RadioGroup";
import PercentageInput from "../../components/inputs/PercentageInput";
import PriceInput from "../../components/inputs/PriceInput";
import { useEffect, useState } from "react";
import CustomSelect from "../../components/customSelect";
import {
  organizationStore,
  useOrganizationStore,
} from "../../store/organization";
import { useEventStore } from "../../store/event";
import {
  GET_PROMO_CODE_ITEMS,
  GET_PROMO_CODE_ITEM_CATEGORIES,
  GET_PROMO_CODE_PK,
  GET_PROMO_CODE_ITEMS_TICKETING,
} from "./queries";
import { Categories, Item } from "../../types/item";
import {
  alphabet,
  appliesToOptions,
  promoCodeTypes,
  promoMethods,
  qrAlhabet,
  triggerRulesOptions,
} from "../../utils";
import { userStore } from "../../store/user";
import { PromoCode } from "../../types/promoCode";
import { usePermissions } from "../auth/api";
import PromoCodeSendModal from "../../components/PromoCodeSendModal";
import AssignAttendeeModal from "../../components/AssignAttendeeModal";
import {PrimaryButton, SecondaryButton, TertiaryButton} from "../../components/Button";
import DeleteModal from "../../components/DeleteModal";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import {
  CUSTOM_UID_SANITIZE,
  normalizeCustomUid,
  validateUidRename,
} from "./customUid";

const client = createApolloClient();
const { getState: getOrganizationStore } = organizationStore;
const { getState: getUserStore } = userStore;

export const promoCodeEditLoader = async ({ params }: LoaderFunctionArgs) => {
  const organizationId = getOrganizationStore().organizationId;
  const user = getUserStore().user;

  const { id } = params;
  const { data } = await client.query({
    query: GET_PROMO_CODE_PK,
    variables: { id },
  });

  let promocodeItemWhere: any = {
    is_active: {
      _eq: true,
    },
  };
  if (organizationId) {
    promocodeItemWhere = {
      ...promocodeItemWhere,
      event: {
        organization_id: {
          _in: [organizationId],
        },
      },
    };
  }
  if (user?.events) {
    promocodeItemWhere = {
      event: {
        ...promocodeItemWhere?.event,
        id: {
          _in: user?.events,
        },
      },
    };
  }

  const { data: itemCategoriesData } = await client.query({
    query: GET_PROMO_CODE_ITEM_CATEGORIES,
    variables: {
      where: organizationId
        ? {
            organization_id: {
              _in: [0, organizationId],
            },
            is_active: {
              _eq: true,
            },
            reference_type: {
              _eq: "item_category",
            },
          }
        : {
            is_active: {
              _eq: true,
            },
            reference_type: {
              _eq: "item_category",
            },
          },
    },
  });

  const { data: itemData } = await client.query({
    query: GET_PROMO_CODE_ITEMS,
    variables: {
      where: promocodeItemWhere,
    },
  });

  const { data: itemDataTicketing } = await client.query({
    query: GET_PROMO_CODE_ITEMS_TICKETING,
    variables: {
      where: promocodeItemWhere,
    },
  });

  return {
    data: data.promo_code_by_pk,
    itemCategories: itemCategoriesData.dashboard_reference,
    items: itemData.items,
    items_ticketing: itemDataTicketing.items_ticketing,
  };
};

export const promoCodeEditAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const { id } = params;
  const user = getUserStore().user;

  const data = await request.formData();
  const inclusiveCategory = data.getAll("inclusive_category");
  const inclusiveItems = data.getAll("inclusive_items");
  const excludedCategory = data.getAll("excluded_category");
  let excludedItems = data.getAll("excluded_items");
  excludedItems = excludedItems?.filter((item) => item);

  const formData = Object.fromEntries(data);
  const quantity = parseInt(`${formData?.quantity || 1}`);
  const methodType = formData?.method_type;
  const promoCodeValueType = formData?.promo_code_value_type;
  const triggerRule = formData?.trigger_rule;
  const spendAmount = formData?.spend_amount;
  const spendQuantity = formData?.spend_quantity;
  const appliesTo = formData?.applies_to;

  // The code text is editable only while no order has used it. Compare against
  // the stored row, not the form, so a stale page cannot skip the usage check.
  const requestedUid = normalizeCustomUid(formData?.uid);
  let uidChange: { uid?: string } = {};
  if (requestedUid) {
    const { data: currentData } = await client.query({
      query: GET_PROMO_CODE_PK,
      variables: { id },
      fetchPolicy: "network-only",
    });
    const current = currentData?.promo_code_by_pk;
    if (current && requestedUid !== current.uid) {
      const renameError = await validateUidRename(client, {
        currentUid: current.uid,
        newUid: requestedUid,
        currentEventId: current.event?.id || 0,
        newEventId: parseInt(`${formData?.event_id || 0}`),
      });
      if (renameError) {
        toast.error(renameError);
        return null;
      }
      uidChange = { uid: requestedUid };
    }
  }


  const percentage = (formData.percentage || "0") as string;
  const percentagePer = (parseFloat(percentage) / 100).toFixed(2);

  const promo_payload = {
    promo_code_value_type: promoCodeValueType,
    trigger: triggerRule
      ? {
          rule: triggerRule,
          spend_amount:
            triggerRule === "spend_threshold" ? +spendAmount * 100 : null,
          spend_quantity:
            triggerRule !== "spend_threshold" ? spendQuantity : null,
          discount_applies_to: appliesTo,
        }
      : null,
    percentage: promoCodeValueType !== "amount_off" ? percentagePer : null,
    amount:
      promoCodeValueType === "amount_off" ? +formData?.amount * 100 : null,
    inclusive_items: inclusiveItems,
    inclusive_category: inclusiveCategory,
    excluded_category: excludedCategory,
    excluded_items: excludedItems,
  };
  const updateData = {
    organization_id: formData.organization_id,
    event_id: formData.event_id,
    attendee_id: formData.attendee_id || null,
    promo_payload,
    promo_code_type: methodType,
    balance: formData.balance,
    unlimited_usage: formData.unlimited_usage,
    description: formData.description,
    last_updated_by: user?.id,
    ...uidChange,
  };

  try {
    await client.mutate({
      mutation: UPDATE_PROMO_CODE,
      variables: { id, data: updateData },
    });
    // if (quantity - 1 === index) {
    toast.success("Promo code update");
    // }
    return redirect("/promocode/list");
  } catch (error) {
    console.log({ error });
    toast.error("Failed to update promo code");
    return redirect("/promocode/list");
  }
  // }
};

export const PromoCodeEdit = () => {
  const navigate = useNavigate();
  const permissions = sessionStorage.getItem("hasuraDefaultRole");
  const organizations = useOrganizationStore((state) => state.organizations);
  const organizationId = useOrganizationStore((state) => state.organizationId);
  const eventId = useEventStore((state) => state.eventId);
  const { fetchAllEvents, allEvents: events } = useEventStore((state) => state);

  const { data, itemCategories, items, items_ticketing } = useLoaderData() as {
    data: PromoCode;
    itemCategories: Categories[];
    items: Item[];
    items_ticketing: Item[];
  };
  const [amount, setAmount] = useState(
    (data?.promo_payload?.amount ?? 0) / 100
  );
  const [spendAmount, setSpendAmount] = useState(
    (data?.promo_payload?.trigger?.spend_amount ?? 0) / 100
  );
  const [unlimitedUsage, setUnlimitedUsage] = useState(data.unlimited_usage);

  const [validationMessage, setValidationMesage] = useState<{
    inclusive_category?: string;
    inclusive_item?: string;
  }>({
    inclusive_category: "",
    inclusive_item: "",
  });
  const [type, setType] = useState(
    data?.promo_payload?.promo_code_value_type || promoCodeTypes?.[0].value
  );
  const [triggerRule, setTriggerRule] = useState(
    data?.promo_payload?.trigger?.rule || triggerRulesOptions?.[0]?.value
  );

  const [appliesTo, setAppliesTo] = useState(appliesToOptions?.[0]?.value);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(
    data?.event?.id || eventId
  );

  const [inclusive, setInclusive] = useState<{
    category: string[];
    item: number[];
  }>({
    category:
      data?.promo_payload?.inclusive_category?.filter((data) => data) || [],
    item: data?.promo_payload?.inclusive_items || [],
  });

  const [exclusive, setExclusive] = useState<{
    category: string[];
    item: number[] | string[];
  }>({
    category: [],
    item: [],
  });
  const [inclusiveOptions, setInclusiveOptions] = useState<{
    category: string[];
    item: number[];
  }>({
    category: [],
    item: [],
  });

  const [exclusiveOptions, setExclusiveOptions] = useState<{
    category: string[];
    item: number[];
  }>({
    category: [],
    item: [],
  });

  useEffect(() => {
    if (data?.id) {
      setSelectedEventId(data?.event?.id || eventId);
      setType(
        data?.promo_payload?.promo_code_value_type || promoCodeTypes?.[0].value
      );
      setTriggerRule(
        data?.promo_payload?.trigger?.rule || triggerRulesOptions?.[0]?.value
      );
      setAppliesTo(
        data?.promo_payload?.trigger?.applies_to || appliesToOptions?.[0]?.value
      );
      setInclusive({
        category:
          data?.promo_payload?.inclusive_category?.filter((data) => data) || [],
        item:
          data?.promo_payload?.inclusive_items?.filter((data) => data) || [],
      });
      setExclusive({
        category:
          data?.promo_payload?.excluded_category?.filter((data) => data) || [],
        item: data?.promo_payload?.excluded_items?.filter((data) => data) || [],
      });
    }
  }, [data]);

  useEffect(() => {
    fetchAllEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      const filteredItems = items.filter(
        (item) => item.event_id === selectedEventId
      );
      const filteredTicketingItems = items_ticketing.filter(
        (item) => item.event_id === selectedEventId
      );
      const allFilteredItems = [...filteredItems, ...filteredTicketingItems];
      setInclusiveOptions((prevState) => ({
        ...prevState,
        item:
          prevState.category.length > 0
            ? allFilteredItems
                .filter((item) => inclusive.category.includes(item.category))
                .map((item) => item.id)
            : allFilteredItems.map((item) => item.id),
      }));
    }
  }, [selectedEventId, inclusive.category, items, items_ticketing]);

  useEffect(() => {
    if (selectedEventId) {
      const filteredItems = items.filter(
        (item) => item.event_id === selectedEventId
      );
      const filteredTicketingItems = items_ticketing.filter(
        (item) => item.event_id === selectedEventId
      );
      const allFilteredItems = [...filteredItems, ...filteredTicketingItems];
      setExclusiveOptions((prevState) => ({
        ...prevState,
        item:
          prevState.category.length > 0
            ? allFilteredItems
                .filter(
                  (item: Item) =>
                    !inclusive.item.includes(item.id) &&
                    inclusive.category.includes(item.category)
                )
                .map((item) => item.id)
            : allFilteredItems
                .filter((item) => !inclusive.item.includes(item.id))
                .map((item) => item.id),
      }));
    }
  }, [selectedEventId, inclusive.item, inclusive.category, items, items_ticketing]);

  useEffect(() => {
    setExclusive((prevState) => ({
      ...prevState,
      category: itemCategories
        .filter(
          (category) => !inclusive.category.includes(category.reference_value)
        )
        .map((category) => category.reference_value),
    }));
  }, [inclusive.category, itemCategories]);
  const { deletePermission } = usePermissions("promocode");
  const fetcher = useFetcher();
  const handleAmountChange = (value: number) => {
    setAmount(value);
  };
  const handleSpendAmountChange = (value: number) => {
    setSpendAmount(value);
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sendType, setSendType] = useState("");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignedAttendeeId, setAssignedAttendeeId] = useState<number | null>(
    data.attendee_id ?? null
  );

  const handleClone = () => {
    navigate(`/promocode/${data.id}/clone`);
  };

  const handleAssignAttendee = async (attendeeId: number) => {
    try {
      await client.mutate({
        mutation: ASSIGN_ATTENDEE_TO_PROMO_CODE,
        variables: {
          id: data.id,
          attendeeId: attendeeId,
        },
      });
      setAssignedAttendeeId(attendeeId);
      toast.success("Attendee assigned successfully");
      navigate(0); // Reload the page to reflect changes
    } catch (error) {
      console.log({ error });
      toast.error("Failed to assign attendee");
    }
  };
  return (
    <Form method="post" action={`/promocode/${data.id}/edit`}>
      <div className="flex justify-end">
        <div className=" py-2">
          <TertiaryButton onClick={handleClone}>Clone Promo Code</TertiaryButton>
          <PrimaryButton
            onClick={() => {
              setSendType("mail");
              setIsModalOpen(true);
            }}
          >
            Send Email
          </PrimaryButton>
          <PrimaryButton
            onClick={() => {
              setSendType("sms");
              setIsModalOpen(true);
            }}
          >
            Send SMS
          </PrimaryButton>
          <PrimaryButton onClick={() => setIsAssignModalOpen(true)}>
            Assign to Attendee
          </PrimaryButton>
          <PromoCodeSendModal
            isOpen={isModalOpen}
            title={"Send Promo Code via "}
            onClose={() => setIsModalOpen(false)}
            type={sendType}
            onConfirm={async ({
              type,
              value,
              setOpen,
              setEmail,
              setPhoneNumber,
              setType,
            }) => {
              try {
                await client.mutate({
                  mutation: PROMO_CODE_SEND_EMAIL_SMS,
                  variables: {
                    data: {
                      uid: data.uid,
                      methodType: data.promo_code_type,
                      email: type === "mail" ? value : undefined,
                      phone_number: type !== "mail" ? value : undefined,
                      id: `${data.id}`,
                      type,
                    },
                  },
                });

                toast.success("Promo sent successfully");
                setOpen(false);
                setEmail("");
                setPhoneNumber("");
                setType("");
                return;
              } catch (error) {
                console.log({ error });
                toast.error("Failed to send promo code" + error);
                return;
              }
            }}
          />
          <AssignAttendeeModal
            isOpen={isAssignModalOpen}
            onClose={() => setIsAssignModalOpen(false)}
            onConfirm={handleAssignAttendee}
          />
        </div>
        {deletePermission && (
          <div className=" py-2">
            <DeleteModal
              onConfirm={() => {
                fetcher.submit(
                  {},
                  {
                    method: "delete",
                    action: `/promocode/${data.id}/delete`,
                  }
                );
              }}
            >
              {({ setOpen }) => (
                <>
                  <SecondaryButton onClick={() => setOpen(true)}>
                    Delete
                  </SecondaryButton>
                </>
              )}
            </DeleteModal>
          </div>
        )}
      </div>
      <FormLayout>
        <div className="col-span-6">
          <Input
            required
            label="Code (UID)"
            type="text"
            name="uid"
            defaultValue={data.uid}
            maxLength={32}
            sanitize={CUSTOM_UID_SANITIZE}
            disabled={data.is_used}
            toolTipShow
            tooltipText="Letters and numbers only, saved in capitals. A code can be renamed only while no order has used it. Orders and reports store the code text, so a rename after use would split its history."
          />
        </div>
        {data.attendee && (
          <div className="col-span-3">
            <p className="block text-lg font-medium text-gray-700">
              Assigned to Attendee:{" "}
              <a
                href={`/attendees/${data.attendee_id}/edit`}
                className="text-stone-700 hover:text-primary-800 underline"
              >
                {data.attendee.first_name} {data.attendee.last_name} ({data.attendee_id})
              </a>
            </p>
          </div>
        )}
        {permissions === "admin" && (
          <div className="col-span-6">
            <CustomSelect
              title="Organization"
              name="organization_id"
              required
              defaultValue={data.organization.id}
              options={organizations.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
            />
          </div>
        )}
        {permissions !== "admin" && (
          <input
            type="hidden"
            value={organizationId ?? ""}
            name="organization_id"
          />
        )}
        <input
          type="hidden"
          value={assignedAttendeeId ?? ""}
          name="attendee_id"
        />
        <div className="col-span-6">
          <CustomSelect
            title="Event"
            name="event_id"
            required
            defaultValue={data.event.id || eventId}
            options={events.map((event) => ({
              label: event.name,
              value: event.id,
            }))}
            onChange={(value) => {
              const eventId = parseInt(value);
              setSelectedEventId(eventId);
            }}
          />
        </div>
        <div className="col-span-6">
          <Input
            defaultValue={data.description}
            label="Description"
            placeholder="Description"
            type="text"
            required
            name="description"
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
            title="Entry Method"
            name="method_type"
            required
            defaultValue={data.promo_code_type}
            options={promoMethods.map((method) => ({
              label: method.label,
              value: method.value,
            }))}
          />
        </div>
        <div className="col-span-6 bg-gray-100 p-2 rounded">
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-1">
              <RadioGroup
                label="Promo Code Type"
                defaultChecked={type}
                name="promo_code_value_type"
                onChange={(value) => setType(value)}
                choices={promoCodeTypes.map((promoCodeType) => ({
                  title: promoCodeType.label,
                  id: promoCodeType.value,
                }))}
              />
            </div>
            <div className="col-span-5">
              <div className="w-full" role="group" aria-labelledby="">
                {type === "percentage_off" ? (
                  <PercentageInput
                    defaultValue={(
                      (data?.promo_payload?.percentage || 1) * 100
                    ).toFixed(2)}
                    label="Percentage"
                    name="percentage"
                  />
                ) : (
                  <PriceInput
                    value={amount}
                    required
                    label="Amount"
                    name="amount"
                    onChange={(value) => handleAmountChange(value ? value : 0)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-6 bg-gray-100 p-2 rounded">
          <div className="grid grid-cols-8 gap-10">
            <div className="col-span-2">
              <RadioGroup
                name="trigger_rule"
                label="Trigger Rule"
                defaultChecked={triggerRule || ""}
                onChange={(value) => setTriggerRule(value)}
                choices={triggerRulesOptions.map((triggerRule) => ({
                  title: triggerRule.label,
                  id: triggerRule.value || "",
                }))}
              />
            </div>
            {triggerRule ? (
              <>
                <div className="col-span-2">
                  <RadioGroup
                    label="Evaluate Against"
                    name="evaluate_against"
                    defaultChecked={
                      data?.promo_payload?.trigger?.evaluate_against ||
                      appliesTo
                    }
                    onChange={(value) => setAppliesTo(value)}
                    choices={appliesToOptions.map((appliesTo) => ({
                      title: appliesTo.label,
                      id: appliesTo.value,
                    }))}
                  />
                </div>
                <div className="col-span-2">
                  {triggerRule === "spend_threshold" ? (
                    <PriceInput
                      required
                      label="Required Spend Amount"
                      name="spend_amount"
                      value={spendAmount}
                      onChange={(value) =>
                        handleSpendAmountChange(value ? value : 0)
                      }
                    />
                  ) : (
                    <Input
                      required
                      label="Required Quantity"
                      placeholder="Quantity"
                      type="number"
                      name="spend_quantity"
                      defaultValue={
                        data?.promo_payload?.trigger?.spend_quantity
                      }
                    />
                  )}
                </div>
                {type === "percentage_off" ? (
                  <div className="col-span-2">
                    <RadioGroup
                      label="Discount Applies To"
                      name="discount_applies_to"
                      defaultChecked={
                        data?.promo_payload?.trigger?.discount_applies_to
                      }
                      onChange={(value) => setAppliesTo(value)}
                      choices={appliesToOptions.map((appliesTo) => ({
                        title: appliesTo.label,
                        id: appliesTo.value,
                      }))}
                    />
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="col-span-6 bg-gray-100 p-2 rounded">
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              <CustomSelect
                isDisabled={
                  inclusive?.item ? inclusive.item?.length > 0 : false
                }
                title="Inclusive Category (OR)"
                name="inclusive_category"
                multi
                defaultValue={data?.promo_payload?.inclusive_category}
                options={[
                  ...(itemCategories.map((category) => ({
                    label: category.reference_label,
                    value: category.reference_value,
                  })) || []),
                ]}
                onChange={(category) =>
                  setInclusive((prevState) => ({
                    ...prevState,
                    category: category as string[],
                  }))
                }
              />
              {triggerRule &&
              appliesTo === "inclusive_only" &&
              (inclusive?.category || inclusive?.item)?.length <= 0 ? (
                <p className="text-red-500">
                  Choose Inclusive Category or Item{" "}
                </p>
              ) : (
                ""
              )}
            </div>
            <div className="col-span-3">
              <CustomSelect
                key={`inclusive-items-${selectedEventId}`}
                isDisabled={
                  inclusive?.category ? inclusive?.category?.length > 0 : false
                }
                defaultValue={data?.promo_payload?.inclusive_items?.map((id: any) =>
                  typeof id === 'string' ? parseInt(id) : id
                )}
                title="Inclusive Items (OR)"
                name="inclusive_items"
                multi
                options={[
                  ...items
                    .filter((item) => item.event_id === selectedEventId)
                    .map((item) => ({
                      label: item.short_name || item.name || "",
                      value: item.id,
                    })),
                  ...items_ticketing
                    .filter((item) => item.event_id === selectedEventId)
                    .map((item) => ({
                      label: item.name || "",
                      value: item.id,
                    }))
                ]}
                onChange={(item) =>
                  setInclusive((prevState) => ({
                    ...prevState,
                    item: item as number[],
                  }))
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-6 mt-2 gap-4">
            <div className="col-span-3">
              <CustomSelect
                defaultValue={data?.promo_payload?.excluded_category}
                title="Excluded Category"
                name="excluded_category"
                multi
                options={[
                  ...(itemCategories
                    .filter(
                      (category) =>
                        !inclusiveOptions.category.includes(
                          category.reference_value
                        )
                    )
                    .map((category) => ({
                      label: category.reference_label,
                      value: category.reference_value,
                    })) || []),
                ]}
                onChange={(category) =>
                  setExclusive((prevState) => ({
                    ...prevState,
                    category: category as string[],
                  }))
                }
              />
            </div>
            <div className="col-span-3">
              <CustomSelect
                key={`excluded-items-${selectedEventId}`}
                title="Excluded Items"
                name="excluded_items"
                multi
                defaultValue={data?.promo_payload?.excluded_items?.map((id: any) =>
                  typeof id === 'string' ? parseInt(id) : id
                )}
                options={(() => {
                  // Preserve previously-saved excluded items in options even
                  // when they no longer match the current inclusive filter,
                  // otherwise react-select drops them from selectValue and
                  // the next save wipes promo_payload.excluded_items.
                  const existingIds = (data?.promo_payload?.excluded_items || [])
                    .map((id: any) =>
                      typeof id === "string" ? parseInt(id) : id
                    );
                  const seen = new Set<number>();
                  const out: { label: string; value: number }[] = [];
                  for (const item of [...items, ...items_ticketing]) {
                    if (item.event_id !== selectedEventId) continue;
                    const matches =
                      !inclusive.item.includes(item.id) &&
                      (inclusive.category.length === 0 ||
                        inclusive.category.includes(item.category));
                    const existing = existingIds.includes(item.id);
                    if ((matches || existing) && !seen.has(item.id)) {
                      seen.add(item.id);
                      out.push({
                        label: item.short_name || item.name || "",
                        value: item.id,
                      });
                    }
                  }
                  return out;
                })()}
                onChange={(item) =>
                  setExclusive((prevState) => ({
                    ...prevState,
                    item: item as number[],
                  }))
                }
              />
            </div>
          </div>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <IndeterminateCheckbox
              label="Unlimited Usage?"
              name="unlimited_usage"
              defaultChecked={data.unlimited_usage}
              onChange={(checked) => setUnlimitedUsage(checked as boolean)}
          />
        </div>
        {!unlimitedUsage && (
            <div className="col-span-4 sm:col-span-2">
              <Input
                  required
                  label="Balance"
                  placeholder="Balance"
                  // A code stored as unlimited has a meaningless balance. The POS
                  // decrements promo_code.balance on every sale even for unlimited
                  // codes (newApiCreateOrders.js applies the unlimited_usage test to
                  // is_used only), so it drifts to 0 and stays there. Pre-filling that
                  // 0 here meant "untick Unlimited, save" wrote balance 0, and the
                  // tablet then refuses the code with reason "no_uses"
                  // (posapp functions/promoLookup.js). Start from the same default the
                  // create form uses, so the operator always sees a usable number.
                  defaultValue={data.unlimited_usage ? 1 : data.balance}
                  type="number"
                  name="balance"
              />
            </div>
        )}
      </FormLayout>
    </Form>
  );
};
